import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, GraduationCap, ArrowLeft, BadgeCheck } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const STATUS_META = {
  pending:  { color: "text-[#F4E8C1]", bg: "bg-[#F4E8C1]/10 border-[#F4E8C1]/25", label: "Pending" },
  approved: { color: "text-[#00E676]", bg: "bg-[#00E676]/10 border-[#00E676]/25", label: "Approved" },
  rejected: { color: "text-red-300",   bg: "bg-red-500/10 border-red-500/25",     label: "Rejected" },
};

export default function LessonRequests() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/lesson-requests");
      setRequests(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (p, action) => {
    try {
      await api.post(`/purchases/${p.purchase_id}/${action}`);
      setRequests((rows) => rows.map((r) => r.purchase_id === p.purchase_id ? { ...r, status: action === "approve" ? "approved" : "rejected" } : r));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed.");
    }
  };

  const filtered = requests.filter((r) => filter === "all" || r.status === filter);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto py-8 px-4 md:px-8">
        <Link to="/lessons" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-[#00E676] mb-5 transition font-mono">
          <ArrowLeft size={14} /> back to lessons
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <GraduationCap size={22} className="text-[#00E676]" strokeWidth={2} />
          <h1 className="font-heading font-bold text-4xl tracking-tight">Lesson requests</h1>
        </div>
        <p className="text-sm text-white/45 font-mono mb-6">Approve or reject access to your paid lessons.</p>

        <div className="surface rounded-xl p-2 mb-5 flex items-center gap-1 w-fit" data-testid="requests-tabs">
          {["pending", "approved", "rejected", "all"].map((k) => (
            <button
              key={k}
              data-testid={`requests-tab-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider transition ${
                filter === k ? "bg-white/10 text-white" : "text-white/45 hover:text-white"
              }`}
            >
              {k} {k === "pending" && requests.filter(r => r.status === "pending").length > 0 && (
                <span className="ml-1 text-[10px] bg-[#F4E8C1]/20 text-[#F4E8C1] px-1.5 rounded">
                  {requests.filter(r => r.status === "pending").length}
                </span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-2">{[1,2,3].map((i) => <div key={i} className="surface rounded-xl h-20 shimmer" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="surface rounded-xl p-12 text-center">
            <BadgeCheck size={32} className="text-[#00E676] mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-heading font-bold text-xl mb-1">All clear.</div>
            <div className="text-sm text-white/50">No {filter !== "all" ? filter : ""} requests right now.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((p, i) => {
              const meta = STATUS_META[p.status] || STATUS_META.pending;
              return (
                <motion.div
                  key={p.purchase_id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="surface rounded-xl p-4 flex items-center gap-4"
                  data-testid={`request-${p.purchase_id}`}
                >
                  <Link to={`/profile/${p.buyer?.user_id}`}>
                    <img src={p.buyer?.picture || `https://api.dicebear.com/9.x/notionists/svg?seed=${p.buyer?.user_id}`} alt=""
                      className="w-10 h-10 rounded-md object-cover bg-white/5" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/profile/${p.buyer?.user_id}`} className="font-semibold hover:text-[#00E676] transition truncate block">
                      {p.buyer?.name || "Anonymous"}
                    </Link>
                    <div className="text-[11px] text-white/45 font-mono truncate">
                      wants <Link to={`/lessons/${p.lesson_id}`} className="text-white/70 hover:text-[#00E676]">{p.lesson_title}</Link> · ${p.amount}
                    </div>
                  </div>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded border ${meta.bg} ${meta.color}`}>
                    {meta.label}
                  </span>
                  {p.status === "pending" && (
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`approve-${p.purchase_id}`} onClick={() => act(p, "approve")}
                        className="cta-green rounded-md px-3 py-1.5 text-xs font-bold flex items-center gap-1">
                        <Check size={12} strokeWidth={2.5} /> Approve
                      </button>
                      <button data-testid={`reject-${p.purchase_id}`} onClick={() => act(p, "reject")}
                        className="rounded-md px-3 py-1.5 text-xs font-bold flex items-center gap-1 bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 transition">
                        <X size={12} strokeWidth={2.5} /> Reject
                      </button>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
